import type { KeyboardOwner } from "../../ui/input/router-types.ts";
import { TOOL_BELT_SLOTS } from "odyssey-shared";
import { say } from "../../words.ts";
import { dropValue } from "../drag/targets.ts";
import { newButtonLook, writeLook, type ButtonLook } from "./button-look.ts";
import { TOUCH_ICONS, TOUCH_ICON_SIZE } from "./icons.ts";
import { TOUCH_BUTTONS, TOUCH_EXTENT, tapTouchButton, type TouchButtonSpec, type TouchHandlers, type TouchView } from "./actions.ts";
import "./buttons.css";

/**
 * What the buttons need from the page.
 */
export interface ButtonsOptions {
  /**
   * The handlers, or null before the scene exists.
   */
  readonly handlers: () => TouchHandlers | null;
  /**
   * What the buttons draw, or null before the scene exists.
   */
  readonly view: () => TouchView | null;
  /**
   * Who owns the keyboard: a tap does nothing unless it is the game, as a key would not.
   */
  readonly owner: () => KeyboardOwner;
  /**
   * Whether the player is in the world (`GameScene.touchLive`): the cluster shows only while it is, and is hidden on the
   * loading screen, the entry screens and any transfer.
   */
  readonly live: () => boolean;
}

/**
 * The mounted cluster.
 */
export interface MountedButtons {
  /**
   * Shows the cluster on a touch page and hides it elsewhere.
   *
   * @param on True to show.
   */
  show(on: boolean): void;
  /**
   * Removes the cluster from the page.
   */
  destroy(): void;
}

/**
 * A button's parts, kept to change only what changed.
 */
export interface Parts {
  readonly spec: TouchButtonSpec;
  readonly element: HTMLButtonElement;
  readonly icon: HTMLImageElement;
  readonly label: HTMLSpanElement;
  readonly look: ButtonLook;
  fingers: number;
}

/**
 * The frame class a button wears.
 *
 * @param primary Whether it is a primary button.
 * @param pressed Whether a finger is on it.
 * @param selected Whether it is latched on.
 * @returns The class.
 */
function frameOf(primary: boolean, pressed: boolean, selected: boolean): string {
  if (primary) {
    return pressed ? "ot-ui-frame-button-primary_pressed" : "ot-ui-frame-button-primary";
  }
  return pressed ? "ot-ui-frame-button_pressed" : selected ? "ot-ui-frame-button_selected" : "ot-ui-frame-button";
}

/**
 * Makes one button's element.
 *
 * @param spec The button.
 * @returns Its parts.
 */
function makeButton(spec: TouchButtonSpec): Parts {
  const element = document.createElement("button");
  element.type = "button";
  element.dataset.touch = spec.id;
  if (spec.id === "tool") {
    element.dataset.drop = dropValue({ surface: "belt", index: TOOL_BELT_SLOTS });
  }
  element.className = `touch-button ot-button ${spec.primary ? "ot-button--primary" : ""}`;
  element.style.width = `calc(${spec.size} * var(--px))`;
  element.style.height = `calc(${spec.size} * var(--px))`;
  element.style.right = `calc(${spec.right} * var(--px))`;
  element.style.bottom = `calc(${spec.bottom} * var(--px))`;
  const icon = document.createElement("img");
  icon.alt = "";
  icon.draggable = false;
  icon.className = "touch-button__icon";
  icon.style.width = `calc(${TOUCH_ICON_SIZE} * var(--px))`;
  icon.style.height = `calc(${TOUCH_ICON_SIZE} * var(--px))`;
  const label = document.createElement("span");
  label.className = "touch-button__label";
  element.append(icon, label);
  return { spec, element, icon, label, look: newButtonLook(), fingers: 0 };
}

/**
 * Draws a button from its look: its frame, accessible name, state attributes, icon and word. Runs only when the look changed.
 *
 * @param part The button.
 */
function drawButton(part: Parts): void {
  const look = part.look;
  const element = part.element;
  element.className = `touch-button ot-button ${part.spec.primary ? "ot-button--primary" : ""} ${frameOf(part.spec.primary, look.down, look.latched)}${look.dimmed ? " is-dimmed" : ""}`;
  element.setAttribute("aria-label", look.name);
  element.setAttribute("aria-pressed", String(look.latched));
  element.setAttribute("aria-disabled", String(look.dimmed));
  const icon = look.icon ?? part.spec.icon;
  if (icon !== null) {
    part.icon.src = TOUCH_ICONS[icon];
  }
  part.label.textContent = look.label;
  part.label.hidden = look.label === "";
}

/**
 * Brings every button up to date with the page's view: each is written in place and redrawn only if its look changed, so a
 * frame of steady play allocates nothing.
 *
 * @param parts The buttons.
 * @param view The page's view.
 */
export function paintButtons(parts: readonly Parts[], view: TouchView): void {
  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index] as Parts;
    if (writeLook(part.look, part.spec.id, view, part.fingers > 0)) {
      drawButton(part);
    }
  }
}

/**
 * Mounts the touch action cluster (ADR-061's amendment of 2026-10-31, decision 7) in the overlay root, hidden until
 * `show(true)` and then only while the player is in the world. A button acts on the press, calls the handler the page gives it, and keeps no keyboard focus; its label,
 * icon and latched or dimmed look follow the page's view, redrawn only when they change.
 *
 * @param root The overlay root.
 * @param options What the cluster needs of the page.
 * @returns The mounted cluster.
 */
export function mountTouchButtons(root: HTMLElement, options: ButtonsOptions): MountedButtons {
  const cluster = document.createElement("div");
  cluster.id = "touch-actions";
  cluster.hidden = true;
  cluster.style.width = `calc(${TOUCH_EXTENT.width} * var(--px))`;
  cluster.style.height = `calc(${TOUCH_EXTENT.height} * var(--px))`;
  cluster.setAttribute("role", "group");
  cluster.setAttribute("aria-label", say("touch.actions"));
  const parts = TOUCH_BUTTONS.map(makeButton);
  const pressed = new Map<number, Parts>();
  for (const part of parts) {
    cluster.append(part.element);
    part.element.addEventListener("pointerdown", (event) => {
      event.preventDefault();
      event.stopPropagation();
      part.element.setPointerCapture(event.pointerId);
      pressed.set(event.pointerId, part);
      part.fingers += 1;
      if (options.owner() === "game") {
        const handlers = options.handlers();
        if (handlers !== null) {
          tapTouchButton(handlers, part.spec.id);
        }
      }
      paint();
    });
    const lift = (event: PointerEvent): void => {
      if (pressed.delete(event.pointerId)) {
        part.fingers -= 1;
      }
      paint();
    };
    part.element.addEventListener("pointerup", lift);
    part.element.addEventListener("pointercancel", lift);
    part.element.addEventListener("lostpointercapture", lift);
    part.element.addEventListener("contextmenu", (event) => event.preventDefault());
  }
  const first = root.firstElementChild;
  if (first === null) {
    root.append(cluster);
  } else {
    first.after(cluster);
  }

  /**
   * Redraws each button whose look changed.
   */
  function paint(): void {
    const view = options.view();
    if (view !== null) {
      paintButtons(parts, view);
    }
  }

  let frame = 0;
  let wanted = false;
  /**
   * Follows the page each frame while wanted: shown while the player is in the world, hidden otherwise.
   */
  const tick = (): void => {
    const visible = wanted && options.live();
    if (cluster.hidden === visible) {
      cluster.hidden = !visible;
      if (!visible) {
        pressed.clear();
        for (const part of parts) {
          part.fingers = 0;
        }
      }
    }
    if (visible) {
      paint();
    }
    frame = requestAnimationFrame(tick);
  };
  return {
    show(on: boolean): void {
      wanted = on;
      cancelAnimationFrame(frame);
      if (on) {
        tick();
      } else {
        cluster.hidden = true;
        pressed.clear();
        for (const part of parts) {
          part.fingers = 0;
        }
      }
    },
    destroy(): void {
      cancelAnimationFrame(frame);
      cluster.remove();
    },
  };
}
